import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import { useTranslation } from 'react-i18next';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import { resolveFileDisplayUrl, useSignedFileUrls } from '@/lib/storageSignedUrl';

interface ResolvedPhoto {
  id: string;
  daily_log_id: string;
  photo_url: string;
  caption: string | null;
  source_photo_id?: string | null;
}

interface FileRef {
  id: string;
  file_url: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

interface ProjectFileRow extends FileRef {
  daily_log_id: string | null;
  log_date: string | null;
  file_name: string | null;
  caption: string | null;
}

interface DailyLogEntry {
  id: string;
  log_date: string;
  title: string | null;
  management_comment: string | null;
  pm_client_comment: string | null;
  visibility: string;
  is_published_to_client: boolean;
  published_at: string | null;
  created_at: string;
  photos: ResolvedPhoto[];
}

type DailyLogRow = Omit<DailyLogEntry, 'photos'>;

function fmtDate(d: string): string {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-NZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

interface Props {
  projectId: string | null;
}

export default function ClientDailyLogEmbed({ projectId }: Props) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<DailyLogEntry[]>([]);
  const [fileRefs, setFileRefs] = useState<FileRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const fileSignedUrls = useSignedFileUrls(fileRefs);

  // ── LIVE project summary ──
  const projectSummary = useProjectSummary(projectId ?? undefined);

  // ── DEBUG: trace status source ──
  useEffect(() => {
    if (!projectId || !projectSummary.loaded) return;
    // eslint-disable-next-line no-console
    console.log('ClientDailyLogEmbed project status debug', {
      projectId,
      summaryProjectStatus: projectSummary.projectStatus,
      summaryActiveStage: projectSummary.activeStage,
      summaryProjectProgress: projectSummary.projectProgress,
      source: 'useProjectSummary',
    });
  }, [projectId, projectSummary.loaded, projectSummary.projectStatus, projectSummary.activeStage, projectSummary.projectProgress]);

  const load = useCallback(async () => {
    if (!projectId) {
      setEntries([]);
      setFileRefs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setImgErrors({});

    try {
      // 1. Fetch published daily logs for this project.
      const { data: logs, error: logsErr } = await supabase
        .from('jphph_daily_logs')
        .select('id, log_date, title, management_comment, pm_client_comment, visibility, is_published_to_client, published_at, created_at')
        .eq('project_id', projectId)
        .eq('is_published_to_client', true)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (logsErr) {
        // eslint-disable-next-line no-console
        console.error('[ClientDailyLogEmbed] Daily log fetch error:', logsErr);
      }

      const logRecords = (logs || []) as DailyLogRow[];
      if (logRecords.length === 0) {
        setEntries([]);
        setFileRefs([]);
        return;
      }

      const logIds = logRecords.map((log) => log.id);
      const logIdSet = new Set(logIds);
      const logIdByDate = new Map<string, string>();
      for (const log of logRecords) {
        if (!logIdByDate.has(log.log_date)) logIdByDate.set(log.log_date, log.id);
      }

      // 2. Load legacy/canonical daily-log photo junction rows.
      const { data: photoRows, error: photoErr } = await supabase
        .from('jphph_daily_log_photos')
        .select('id, daily_log_id, source_photo_id, photo_url, caption')
        .in('daily_log_id', logIds)
        .order('created_at', { ascending: true });

      if (photoErr) {
        // eslint-disable-next-line no-console
        console.error('[ClientDailyLogEmbed] Photo junction fetch error:', photoErr);
      }

      // 3. Load client-visible project-file rows directly.
      // This is the authoritative fallback when older shares did not create a
      // jphph_daily_log_photos junction row, or created it against another row
      // for the same project/date.
      const { data: projectFileRows, error: projectFileErr } = await supabase
        .from('jphph_project_files')
        .select('id, daily_log_id, log_date, file_name, caption, file_url, storage_bucket, storage_path, is_deleted, is_visible, is_client_visible')
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .or('is_visible.eq.true,is_visible.is.null')
        .eq('is_client_visible', true)
        .order('created_at', { ascending: true });

      if (projectFileErr) {
        // eslint-disable-next-line no-console
        console.error('[ClientDailyLogEmbed] Client-visible project file fetch error:', projectFileErr);
      }

      const visibleProjectFiles = ((projectFileRows || []) as ProjectFileRow[]).filter((file) => {
        if (file.daily_log_id && logIdSet.has(file.daily_log_id)) return true;
        return Boolean(file.log_date && logIdByDate.has(file.log_date));
      });

      const fileRefMap = new Map<string, ProjectFileRow>();
      for (const file of visibleProjectFiles) {
        fileRefMap.set(file.id, file);
      }
      setFileRefs(
        visibleProjectFiles.map((file) => ({
          id: file.id,
          file_url: file.file_url,
          storage_bucket: file.storage_bucket,
          storage_path: file.storage_path,
        }))
      );

      // 4. Merge direct project-file rows and junction rows, deduped by source
      // project-file id. Client visibility on jphph_project_files is always the
      // gate for source-linked photos.
      const photosByLog = new Map<string, ResolvedPhoto[]>();
      const seenByLog = new Map<string, Set<string>>();

      const addPhoto = (logId: string, photo: ResolvedPhoto, dedupeKey: string) => {
        if (!logIdSet.has(logId)) return;
        const seen = seenByLog.get(logId) || new Set<string>();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        seenByLog.set(logId, seen);

        const list = photosByLog.get(logId) || [];
        list.push(photo);
        photosByLog.set(logId, list);
      };

      for (const file of visibleProjectFiles) {
        const logId =
          file.daily_log_id && logIdSet.has(file.daily_log_id)
            ? file.daily_log_id
            : file.log_date
              ? logIdByDate.get(file.log_date) || null
              : null;
        if (!logId) continue;

        addPhoto(
          logId,
          {
            id: file.id,
            daily_log_id: logId,
            photo_url: file.file_url || '',
            caption: file.caption || file.file_name,
            source_photo_id: file.id,
          },
          `source:${file.id}`
        );
      }

      for (const row of photoRows || []) {
        if (!logIdSet.has(row.daily_log_id)) continue;

        if (row.source_photo_id) {
          const visibleSource = fileRefMap.get(row.source_photo_id);
          if (!visibleSource) {
            // Never expose a source-linked photo unless the project-file row is
            // explicitly client-visible.
            continue;
          }

          addPhoto(
            row.daily_log_id,
            {
              id: row.id,
              daily_log_id: row.daily_log_id,
              photo_url: visibleSource.file_url || row.photo_url || '',
              caption: row.caption || visibleSource.caption || visibleSource.file_name,
              source_photo_id: row.source_photo_id,
            },
            `source:${row.source_photo_id}`
          );
          continue;
        }

        // Legacy rows without source_photo_id can only use their stored URL.
        if (!row.photo_url) continue;
        addPhoto(
          row.daily_log_id,
          {
            id: row.id,
            daily_log_id: row.daily_log_id,
            photo_url: row.photo_url,
            caption: row.caption,
            source_photo_id: null,
          },
          `legacy:${row.id}`
        );
      }

      const enrichedEntries: DailyLogEntry[] = logRecords.map((log) => ({
        ...log,
        photos: photosByLog.get(log.id) || [],
      }));

      setEntries(enrichedEntries);

      // eslint-disable-next-line no-console
      console.log('[ClientDailyLogEmbed] Load complete', {
        projectId,
        logCount: enrichedEntries.length,
        totalPhotos: enrichedEntries.reduce((sum, entry) => sum + entry.photos.length, 0),
        directVisibleFiles: visibleProjectFiles.length,
        junctionRows: (photoRows || []).length,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ClientDailyLogEmbed] Unexpected load error:', err);
      setEntries([]);
      setFileRefs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const getPhotoDisplayUrl = useCallback((photo: ResolvedPhoto): string => {
    if (photo.source_photo_id && fileSignedUrls[photo.source_photo_id]) {
      return fileSignedUrls[photo.source_photo_id];
    }
    return resolveFileDisplayUrl({ file_url: photo.photo_url });
  }, [fileSignedUrls]);

  const openLightbox = useCallback((photos: ResolvedPhoto[], selectedPhotoId: string) => {
    const resolved = photos
      .map((photo) => ({
        photo,
        item: {
          url: getPhotoDisplayUrl(photo),
          caption: photo.caption || '',
        } satisfies LightboxPhoto,
      }))
      .filter(({ item }) => Boolean(item.url));

    if (resolved.length === 0) return;
    const selectedIndex = resolved.findIndex(({ photo }) => photo.id === selectedPhotoId);
    setLightboxPhotos(resolved.map(({ item }) => item));
    setLightboxIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [getPhotoDisplayUrl]);

  const handleImageError = useCallback((photoId: string, url: string) => {
    setImgErrors((prev) => {
      if (prev[photoId]) return prev;
      return { ...prev, [photoId]: true };
    });
    // eslint-disable-next-line no-console
    console.warn('[ClientDailyLogEmbed] Image load FAILED', { photoId, url });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
          <i className="ri-camera-line text-5xl" />
        </div>
        <p className="text-[#374151] font-semibold text-sm">{t('embed.dailyLog.noSiteUpdates')}</p>
        <p className="text-[#9CA3AF] text-xs mt-1 max-w-xs mx-auto">
          {t('embed.dailyLog.noSiteUpdatesDesc')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {lightboxIndex !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => {
            setLightboxIndex(null);
            setLightboxPhotos([]);
          }}
        />
      )}

      {/* ── Live Project Status banner ── */}
      {projectSummary.loaded && (
        <div
          className="rounded-2xl border p-4 flex items-center justify-between gap-3"
          style={{
            backgroundColor: projectSummary.projectProgress === 100 ? '#F0FDF4' : '#FEF0E7',
            borderColor: projectSummary.projectProgress === 100 ? '#BBF7D0' : '#FDBA74',
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
              style={{
                backgroundColor: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A',
              }}
            >
              <i className="ri-check-line text-white text-xs" />
            </div>
            <div className="min-w-0">
              <p
                className="text-xs font-bold truncate"
                style={{ color: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A' }}
              >
                {projectSummary.projectProgress === 100
                  ? 'Current Project Status: Completed'
                  : `Current Project Status: ${projectSummary.activeStage || projectSummary.projectStatus}`}
              </p>
              <p className="text-[#6B7280] text-[10px] mt-0.5">
                Overall {projectSummary.projectProgress}% · Schedule {projectSummary.scheduleFinalProgress}% · Checklist {projectSummary.checklistFinalProgress}%
              </p>
            </div>
          </div>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
            style={{
              backgroundColor: projectSummary.projectProgress === 100 ? '#F0FDF4' : '#FEF0E7',
              color: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A',
            }}
          >
            {projectSummary.projectProgress}%
          </span>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-[#F0FDF4] rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
          <i className="ri-camera-line text-white text-sm" />
        </div>
        <div>
          <p className="text-[#059669] text-sm font-bold">{t('embed.dailyLog.infoBanner')}</p>
          <p className="text-[#374151] text-xs mt-0.5 leading-relaxed">
            {t('embed.dailyLog.infoBannerDesc')}
          </p>
        </div>
      </div>

      {entries.map((entry) => (
        <div key={entry.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Day header */}
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-[#1A1A1A] text-sm font-bold">{fmtDate(entry.log_date)}</p>
            {entry.title && (
              <p className="text-[#6B7280] text-xs mt-0.5">{entry.title}</p>
            )}
            {entry.pm_client_comment && (
              <div className="mt-2 rounded-xl bg-[#F0FDF4] border border-[#D1FAE5] px-3 py-2">
                <p className="text-[#047857] text-[10px] font-bold uppercase tracking-wide">Japan Homes update</p>
                <p className="text-[#374151] text-xs mt-1 leading-relaxed whitespace-pre-line">
                  {entry.pm_client_comment}
                </p>
              </div>
            )}
            {entry.management_comment && (
              <p className="text-[#374151] text-xs mt-2 leading-relaxed whitespace-pre-line">{entry.management_comment}</p>
            )}
          </div>

          {/* Photo grid */}
          {entry.photos.length > 0 && (
            <div className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {entry.photos.map((photo) => {
                  const hasError = imgErrors[photo.id];
                  const displayUrl = getPhotoDisplayUrl(photo);

                  return (
                    <button
                      type="button"
                      key={photo.id}
                      onClick={() => openLightbox(entry.photos, photo.id)}
                      disabled={hasError || !displayUrl}
                      className="w-full aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group relative disabled:cursor-default bg-slate-100"
                    >
                      {hasError ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-[#9CA3AF]">
                          <i className="ri-image-line text-2xl mb-1" />
                          <span className="text-[10px]">Photo unavailable</span>
                        </div>
                      ) : !displayUrl ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-[#9CA3AF]">
                          <i className="ri-loader-4-line animate-spin text-lg mb-1" />
                          <span className="text-[10px]">Loading photo...</span>
                        </div>
                      ) : (
                        <ProjectPhotoImage
                          src={displayUrl}
                          alt={photo.caption || 'Site photo'}
                          sizes="(max-width: 768px) 90vw, 360px"
                          loading="lazy"
                          debugContext="ClientDailyLogEmbed"
                          className="daily-log-photo-img"
                          onError={() => handleImageError(photo.id, displayUrl)}
                        />
                      )}
                      {!hasError && displayUrl && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <i className="ri-zoom-in-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Captions */}
              {entry.photos.some((photo) => photo.caption) && (
                <div className="mt-3 space-y-1.5">
                  {entry.photos
                    .filter((photo) => photo.caption)
                    .map((photo) => (
                      <div key={photo.id} className="flex items-start gap-2">
                        <i className="ri-chat-1-line text-[#9CA3AF] text-xs mt-0.5 flex-shrink-0" />
                        <p className="text-[#6B7280] text-xs leading-relaxed">{photo.caption}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
